/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unsafe-enum-comparison, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-floating-promises, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import { useEffect, useState, useMemo } from 'react';
import { SetterOrUpdater } from 'recoil';
import {
  Box,
  Collapse,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import { IndicesTable } from './IndicesTable';
import useConcentration from '../../../hooks/indices/useConcentration';
import { ErrorContent } from '../../shared';
import {
  StrikePutCallConcentration,
  PutCall,
  StrikeConcentration,
  IndicesContentType,
  SymSelectorSettings,
} from '../../../types';
import { nullsToEndComparator, formatAsCurrency } from '../../../util';
import { IndicesHeader } from '../shared';
import {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  gridNumberComparator,
} from '@spotgamma/x-data-grid-premium';

interface StrikeConcentrationTableProps {
  selectedSym: string;
  containerStyleOverrides?: SxProps<Theme>;
  isUnfolded?: boolean;
  setIsUnfolded?: SetterOrUpdater<boolean>;
  symSelectorSettings?: SymSelectorSettings;
}

export const StrikeConcentrationTable = ({
  containerStyleOverrides,
  selectedSym,
  isUnfolded,
  setIsUnfolded,
  symSelectorSettings,
}: StrikeConcentrationTableProps) => {
  const { getConcentrationByStrike } = useConcentration();
  const [data, setData] = useState<any[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const theme = useTheme();

  useEffect(() => {
    async function generateTableData() {
      setIsLoading(true);
      setData([]);
      const data = await getConcentrationByStrike(selectedSym);
      let totalDelta = 0;
      let totalGamma = 0;
      const groupedData = (data as StrikeConcentration[]).reduce(
        (groups, { strike, type, oi, delta, gamma }) => {
          let group = groups.get(strike) ?? { delta: 0, gamma: 0, strike: 0 };
          totalDelta = Math.abs(delta) + totalDelta;
          totalGamma = Math.abs(gamma) + totalGamma;
          group = {
            ...group,
            strike,
            delta: group.delta + Math.abs(delta),
            gamma: group.gamma + Math.abs(gamma),
            ...(type === PutCall.CALL
              ? {
                  call_delta: Math.abs(delta),
                  call_gamma: Math.abs(gamma),
                  call: oi,
                }
              : { put_delta: delta, put_gamma: gamma, put: oi }),
          };
          return groups.set(strike, group as StrikePutCallConcentration);
        },
        new Map<number, StrikePutCallConcentration>(),
      );
      setIsLoading(false);
      setData(
        Array.from(groupedData.values()).map((d) => ({
          ...d,
          id: d.strike,
          percent_gamma: (d.gamma / totalGamma || 0) * 100,
          percent_delta: (d.delta / totalDelta || 0) * 100,
        })),
      );
    }
    generateTableData();
  }, [getConcentrationByStrike, selectedSym]);

  const defaultHeaderTitleStyles = useMemo(
    () => ({
      fontSize: isMobile ? 11 : 14,
      color: theme.palette.sgGreen,
      textTransform: 'capitalize',
      fontWeight: 'bold',
      whiteSpace: 'normal',
      lineHeight: 'normal',
    }),
    [isMobile, theme.palette.sgGreen],
  );

  const columns = useMemo(
    () =>
      [
        {
          headerName: 'Strike',
          headerClassName: 'grid-header-cell',
          field: 'strike',
          flex: 1,
          minWidth: 80,
          type: 'number',
          getSortComparator: nullsToEndComparator(gridNumberComparator),
          renderHeader: (_params: GridColumnHeaderParams) => (
            <Typography
              sx={{
                ...defaultHeaderTitleStyles,
              }}
            >
              Strike
            </Typography>
          ),
          renderCell: (params: GridRenderCellParams) => {
            return <Typography>{params.value}</Typography>;
          },
        },
        {
          headerName: 'Put',
          headerClassName: 'grid-header-cell',
          field: 'put',
          flex: 1,
          minWidth: 80,
          type: 'number',
          getSortComparator: nullsToEndComparator(gridNumberComparator),
          renderHeader: (_params: GridColumnHeaderParams) => (
            <Typography
              sx={{
                ...defaultHeaderTitleStyles,
              }}
            >
              Put
            </Typography>
          ),
          renderCell: (params: GridRenderCellParams) => {
            return (
              <Typography>
                {params.value && Math.round(params.value).toLocaleString()}
              </Typography>
            );
          },
        },
        {
          headerName: 'Call',
          headerClassName: 'grid-header-cell',
          field: 'call',
          flex: 1,
          minWidth: 80,
          type: 'number',
          getSortComparator: nullsToEndComparator(gridNumberComparator),
          renderHeader: (_params: GridColumnHeaderParams) => (
            <Typography
              sx={{
                ...defaultHeaderTitleStyles,
              }}
            >
              Call
            </Typography>
          ),
          renderCell: (params: GridRenderCellParams) => {
            return (
              <Typography>
                {params.value && Math.round(params.value).toLocaleString()}
              </Typography>
            );
          },
        },
        {
          headerName: 'Gamma Notional',
          headerClassName: 'grid-header-cell',
          field: 'gamma',
          flex: 1,
          minWidth: 135,
          type: 'number',
          getSortComparator: nullsToEndComparator(gridNumberComparator),
          renderHeader: (_params: GridColumnHeaderParams) => (
            <Typography
              sx={{
                ...defaultHeaderTitleStyles,
              }}
            >
              Gamma Notional
            </Typography>
          ),
          renderCell: (params: GridRenderCellParams) => {
            return (
              <Typography>
                {isNaN(params.value)
                  ? ''
                  : formatAsCurrency(params.value, true)}
              </Typography>
            );
          },
        },
        {
          headerName: 'Delta Notional',
          headerClassName: 'grid-header-cell',
          field: 'delta',
          flex: 1,
          minWidth: 135,
          type: 'number',
          getSortComparator: nullsToEndComparator(gridNumberComparator),
          renderHeader: (_params: GridColumnHeaderParams) => (
            <Typography
              sx={{
                ...defaultHeaderTitleStyles,
              }}
            >
              Delta Notional
            </Typography>
          ),
          renderCell: (params: GridRenderCellParams) => {
            return (
              <Typography>
                {isNaN(params.value)
                  ? ''
                  : formatAsCurrency(params.value, true)}
              </Typography>
            );
          },
        },
        {
          headerName: '% Delta',
          headerClassName: 'grid-header-cell',
          field: 'percent_delta',
          flex: 1,
          minWidth: 90,
          type: 'number',
          getSortComparator: nullsToEndComparator(gridNumberComparator),
          renderHeader: (_params: GridColumnHeaderParams) => (
            <Typography
              sx={{
                ...defaultHeaderTitleStyles,
              }}
            >
              % Delta
            </Typography>
          ),
          renderCell: (params: GridRenderCellParams) => {
            return <Typography>{`${params.value.toFixed(2)}%`}</Typography>;
          },
        },
        {
          headerName: '% Gamma',
          headerClassName: 'grid-header-cell',
          field: 'percent_gamma',
          flex: 1,
          minWidth: 95,
          type: 'number',
          getSortComparator: nullsToEndComparator(gridNumberComparator),
          renderHeader: (_params: GridColumnHeaderParams) => (
            <Typography
              sx={{
                ...defaultHeaderTitleStyles,
              }}
            >
              % Gamma
            </Typography>
          ),
          renderCell: (params: GridRenderCellParams) => {
            return <Typography>{`${params.value.toFixed(2)}%`}</Typography>;
          },
        },
      ] as GridColDef[],
    [defaultHeaderTitleStyles],
  );

  if (data == null) {
    return <ErrorContent />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: '8px',
        ...containerStyleOverrides,
      }}
    >
      <IndicesHeader
        type={IndicesContentType.STRIKE_CONCENTRATION_TABLE}
        title="Strike Table"
        expandable
        isUnfolded={isUnfolded}
        setIsUnfolded={setIsUnfolded}
        shouldHideBtns={isUnfolded === false}
        symbol={selectedSym}
        symSelectorSettings={symSelectorSettings}
      />
      <Collapse in={isUnfolded} unmountOnExit>
        <Box
          sx={{
            overflow: 'auto',
            height: '75vh',
            display: isUnfolded != null && setIsUnfolded ? 'flex' : 'none',
          }}
        >
          <IndicesTable data={data} columns={columns} isLoading={isLoading} />
        </Box>
      </Collapse>

      {!isUnfolded && !setIsUnfolded && (
        <Box
          sx={{
            overflow: 'auto',
          }}
        >
          <IndicesTable data={data} columns={columns} isLoading={isLoading} />
        </Box>
      )}
    </Box>
  );
};
